// src/markup/pickMarkSet.js — which set of mark types the markup box offers.
// ---------------------------------------------------------------------------
// 2026-08-17. The catalogue (Settings → Mark types) holds several SETS. Two
// different parts of the app read it and they want opposite things:
//
//   • Pin colours (utils/useTrades.js) want the PIN vocabulary — who is on the
//     hook for a defect. That is a trade: Plumbing, Tiling, Carpentry.
//   • The markup box wants the MEASURING set — what work the marked area is,
//     because that is what rolls up in m² and gets priced. Retiling. Full
//     regrout. Adhoc grouting.
//
// useTrades.js was written with its rule explicit ("A tiling set of areas is a
// measuring toolset, not a pin vocabulary"). The markup box was not: it took
// the first set with any active type, the trades set is first, and so the
// markup box listed trades. Brett, seeing it: "that is wrong should be
// something like, retiling, full regrout, adhoc grouting."
//
// Pure and separate from the screen so the rule can be tested, and so the next
// reader finds the two halves of the decision written down in one place.

/** A type that can be measured — the markup box's business. */
export function isMeasurable(type) {
  return !!type && (type.kind === 'area' || type.kind === 'length');
}

/** Retired types stay readable on existing marks but are never offered again. */
export function isOfferable(type) {
  return !!type && type.active !== false;
}

/**
 * Choose the set whose types the markup toolbar should offer.
 *
 * Preference order, each one a deliberate fallback rather than an accident:
 *   1. a set with at least one offerable MEASURABLE type — the real answer;
 *   2. failing that, any set with an offerable type — better a pin vocabulary
 *      than an empty toolbar on a tenant who has only ever made pins;
 *   3. failing that, the first set at all, so a catalogue of entirely retired
 *      types still shows what it contains.
 *
 * @param {Array} sets catalogue sets, as /api/mark-catalogue returns them
 * @returns {object|null}
 */
export function pickMarkSet(sets) {
  if (!Array.isArray(sets) || !sets.length) return null;
  const has = (fn) => (s) => Array.isArray(s.types) && s.types.some(fn);
  return (
    sets.find(has((t) => isMeasurable(t) && isOfferable(t)))
    || sets.find(has(isOfferable))
    || sets[0]
    || null
  );
}

/** The types to put in the toolbar: the chosen set's, minus the retired ones. */
export function offerableTypes(sets) {
  const set = pickMarkSet(sets);
  return ((set && set.types) || []).filter(isOfferable);
}
